import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FiFile, FiDownload, FiX } from 'react-icons/fi';
import './UploadTemplateModal.css';

interface UploadTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: FileList | null) => void;
  title?: string;
  description?: string;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in MB
  templateFile?: string; // URL to template file
  templateFileName?: string;
  showFileInput?: boolean;
  customTemplateContent?: React.ReactNode;
}

const UploadTemplateModal: React.FC<UploadTemplateModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  title,
  description,
  acceptedFileTypes = ['.xlsx', '.xls', '.pdf', '.doc', '.docx'],
  maxFileSize = 10,
  templateFile,
  templateFileName = 'Template.xlsx',
  showFileInput = true,
  customTemplateContent
}) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [error, setError] = useState<string>('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setError('');

    if (files && files.length > 0) {
      // Validate file types
      const invalidFiles = Array.from(files).filter(file => {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        return !acceptedFileTypes.includes(fileExtension);
      });

      if (invalidFiles.length > 0) {
        setError(String(t('upload.invalid_file_type', { types: acceptedFileTypes.join(', ') })));
        return;
      }

      // Validate file size
      const oversizedFiles = Array.from(files).filter(file => 
        file.size > maxFileSize * 1024 * 1024
      );

      if (oversizedFiles.length > 0) {
        setError(String(t('upload.file_too_large', { maxSize: maxFileSize })));
        return;
      }

      setSelectedFiles(files);
    }
  };

  const handleUpload = () => {
    if (selectedFiles) {
      onUpload(selectedFiles);
      setSelectedFiles(null);
      setError('');
      onClose();
    }
  };

  const handleDownloadTemplate = () => {
    if (templateFile) {
      const link = document.createElement('a');
      link.href = templateFile;
      link.download = templateFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const removeFile = (index: number) => {
    if (selectedFiles) {
      const dt = new DataTransfer();
      Array.from(selectedFiles).forEach((file, i) => {
        if (i !== index) dt.items.add(file);
      });
      setSelectedFiles(dt.files);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" className="upload-template-modal wide-modal">
      <ModalHeader toggle={onClose}>
        <div className="d-flex align-items-center mr-2 ml-2">
          <FiFile className="me-2" size={20} />
          {title || t('upload.upload_template_title')}
        </div>
      </ModalHeader>
      
      <ModalBody>
        <div className="upload-template-content">
          {/* Template Information */}
          <div className="template-info-section mb-4">
            <h6 className="text-primary mb-3">
              <FiFile className="me-2" />
              {t('upload.template_guide')}
            </h6>
            
            <div className="template-description mb-3">
              <p className="text-muted">
                { t('institutions.template_description')}
              </p>
            </div>

            {/* Custom template content or default template */}
            {customTemplateContent ? (
              customTemplateContent
            ) : (
              <div className="template-example">
                <div className="template-card p-3 border rounded">
                  <h6 className="mb-2">{t('upload.template_structure')}</h6>
                  <ul className="template-list">
                    <li>{t('upload.template_column_headers')}</li>
                    <li>{t('upload.template_data_format')}</li>
                    <li>{t('upload.template_required_fields')}</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Download Template Button */}
            {templateFile && (
              <div className="template-download mt-3">
                <Button 
                  color="outline-primary" 
                  onClick={handleDownloadTemplate}
                  className="d-flex align-items-center"
                >
                  <FiDownload className="me-2" />
                  {t('upload.download_template')}
                </Button>
              </div>
            )}
          </div>

          {/* File Requirements */}
          <div className="file-requirements mb-4">
            <h6 className="text-info mb-2">
              {t('upload.file_requirements')}
            </h6>
            <div className="requirements-list">
              <div className="requirement-item">
                <span className="requirement-label">{t('upload.accepted_formats')}</span>
                <span className="requirement-value">{acceptedFileTypes.join(', ')}</span>
              </div>
              <div className="requirement-item">
                <span className="requirement-label">{t('upload.max_file_size')}</span>
                <span className="requirement-value">{maxFileSize} MB</span>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {/* File Upload Section */}
          {showFileInput && (
            <div className="file-upload-section">
              <h6 className="mb-3">{t('upload.select_file')}</h6>
              
              {/* File Input */}
              <div className="file-input-wrapper mb-3">
                <input
                  type="file"
                
                  accept={acceptedFileTypes.join(',')}
                  onChange={handleFileSelect}
                  className="form-control"
                  id="file-upload-input"
                />
              </div>

              {/* Selected Files Display */}
              {selectedFiles && selectedFiles.length > 0 && (
                <div className="selected-files">
                  <h6 className="mb-2">{t('upload.selected_files')}:</h6>
                  <div className="file-list">
                    {Array.from(selectedFiles).map((file, index) => (
                      <div key={index} className="file-item d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                        <div className="d-flex align-items-center">
                          <FiFile className="me-2 text-primary" />
                          <span className="file-name">{file.name}</span>
                          <span className="file-size text-muted ms-2">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-danger p-0"
                        >
                          <FiX size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
        {showFileInput && (
          <Button 
            color="primary" 
            onClick={handleUpload}
            disabled={!selectedFiles || selectedFiles.length === 0}
          >
            {t('upload_file')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UploadTemplateModal; 